import type { PlayerInventorySlot } from "../queries/usePlayerInventoryQuery";

// Tool strength order (higher index = stronger)
const TOOL_STRENGTH_ORDER = [
  "Wooden", // 0
  "Copper", // 1
  "Iron", // 2
  "Gold", // 3
  "Diamond", // 4
  "Neptunium", // 5
] as const;

// Wood/log types that should use axe
const WOOD_TYPES = [
  50, // OakLog
  51, // BirchLog
  52, // JungleLog
  53, // SakuraLog
  54, // AcaciaLog
  55, // SpruceLog
  56, // DarkOakLog
  57, // MangroveLog
  32794, // AnyLog
];

// Tool type IDs
const TOOL_IDS = {
  // Picks
  WoodenPick: 32768,
  CopperPick: 32769,
  IronPick: 32770,
  GoldPick: 32771,
  DiamondPick: 32772,
  NeptuniumPick: 32773,
  // Axes
  WoodenAxe: 32774,
  CopperAxe: 32775,
  IronAxe: 32776,
  GoldAxe: 32777,
  DiamondAxe: 32778,
  NeptuniumAxe: 32779,
} as const;

function getToolStrength(toolName: string): number {
  for (let i = 0; i < TOOL_STRENGTH_ORDER.length; i++) {
    if (toolName.includes(TOOL_STRENGTH_ORDER[i])) {
      return i;
    }
  }
  return -1;
}

function getToolName(toolId: number): string | null {
  for (const [name, id] of Object.entries(TOOL_IDS)) {
    if (id === toolId) {
      return name;
    }
  }
  return null;
}

function isWoodType(objectTypeId: number): boolean {
  return WOOD_TYPES.includes(objectTypeId);
}

function isTool(toolId: number): boolean {
  return Object.values(TOOL_IDS).includes(
    toolId as (typeof TOOL_IDS)[keyof typeof TOOL_IDS],
  );
}

function isAxe(toolId: number): boolean {
  const toolName = getToolName(toolId);
  return toolName?.includes("Axe") ?? false;
}

function isPick(toolId: number): boolean {
  const toolName = getToolName(toolId);
  return toolName?.includes("Pick") ?? false;
}

export function getBestTool(
  playerInventory: PlayerInventorySlot[] | undefined,
  targetObjectTypeId: number,
): number | null {
  if (!playerInventory) return null;

  const shouldUseAxe = isWoodType(targetObjectTypeId);

  let bestTool: PlayerInventorySlot | null = null;
  let bestStrength = -1;

  for (const slot of playerInventory) {
    if (!isTool(slot.objectTypeId)) continue;

    const isCorrectToolType = shouldUseAxe
      ? isAxe(slot.objectTypeId)
      : isPick(slot.objectTypeId);

    if (!isCorrectToolType) continue;

    const toolName = getToolName(slot.objectTypeId);
    if (!toolName) continue;

    const strength = getToolStrength(toolName);
    if (strength > bestStrength) {
      bestStrength = strength;
      bestTool = slot;
    }
  }

  return bestTool?.slot ?? null;
}
